import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Button } from '@components/common/Button';
import { useConsultationSendMutation } from '@services/consultationApi';
import { store } from '@store/index';
import { getApiError } from '@utils/apiError';
import { theme } from '@styles/theme';
import type { ConsultationScope, ConsultationSent } from '#types/consultation';

const Form = styled.form`display: grid; gap: 10px; textarea { width: 100%; min-height: 96px; resize: vertical; padding: 12px; border: 1px solid ${theme.colors.border.default}; border-radius: 12px; font: inherit; }`;
const Actions = styled.div`display: flex; justify-content: space-between; gap: 12px; align-items: center; color: ${theme.colors.text.secondary}; font-size: 13px;`;
export const ErrorText = styled.p`color: ${theme.colors.error}; margin: 8px 0;`;

export const normalizeConsultationText = (text: string): string => text.replace(/\r\n/g, '\n').replace(/^\p{White_Space}+|\p{White_Space}+$/gu, '');

export function ConsultationComposer({ scope, onSent, disabled = false }: {
  scope: ConsultationScope; onSent(result: ConsultationSent): Promise<void>; disabled?: boolean;
}) {
  const { t } = useTranslation('consultation');
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const requestId = useRef<string>();
  const inFlight = useRef(false);
  const [send, result] = useConsultationSendMutation();
  const count = Array.from(normalizeConsultationText(draft)).length;
  const submit = async () => {
    if (inFlight.current || disabled || store.getState().user.authEpoch !== scope.authEpoch) return;
    const content = normalizeConsultationText(draft);
    if (!content || count > 2000) { setError(t('invalidText')); return; }
    inFlight.current = true;
    setError('');
    // Generated before the request; kept through timeouts/retries. Explicit edits start a new logical send.
    requestId.current ??= crypto.randomUUID();
    let saved = false;
    try {
      const sent = await send({ ...scope, content, clientMessageId: requestId.current }).unwrap();
      if (store.getState().user.authEpoch !== scope.authEpoch) return;
      saved = true;
      setDraft(''); requestId.current = undefined;
      await onSent(sent);
    } catch (failure) {
      if (store.getState().user.authEpoch !== scope.authEpoch) return;
      const code = getApiError(failure).code;
      setError(t(saved ? 'sentRefreshFailed' : code === 'CONSULTATION_REQUEST_CONFLICT' ? 'conflict' : 'sendFailed'));
    } finally { inFlight.current = false; }
  };
  return <Form onSubmit={(event) => { event.preventDefault(); void submit(); }}>
    <label>{t('message')}<textarea aria-label={t('message')} value={draft} disabled={disabled || result.isLoading}
      placeholder={t('placeholder')} onChange={(event) => { setDraft(event.target.value); requestId.current = undefined; setError(''); }}
      onKeyDown={(event) => { if (event.key === 'Enter' && (event.ctrlKey || event.metaKey) && !event.nativeEvent.isComposing) { event.preventDefault(); void submit(); } }} /></label>
    <Actions><span>{count} / 2000 · {t('shortcut')}</span><Button type="submit" disabled={disabled || !count || count > 2000} isLoading={result.isLoading}>{t('send')}</Button></Actions>
    {error && <ErrorText role="alert">{error}</ErrorText>}
  </Form>;
}
